import { APP_INITIALIZER, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgxsModule } from '@ngxs/store';
import { ActionRequiredState, AppEnvironmentState, PermissionsState, FeatureFlagConfigurationState, UserState, LIBRARY_CONFIG, OfflineHeaderModule, SidebarV3Component } from '@gea/digital-ui-lib';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { MessageService } from 'primeng/api';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { providePrimeNG } from 'primeng/config';
import { PrimeNGModule } from './pages/core/shared/prime-ng.module';
import { GeaLibraryModule } from './pages/core/shared/gea-library.module';
import { SharedModule } from './pages/core/shared/shared.module';
import { cSInterceptorInterceptor } from './pages/core/shared/interceptors/csinterceptor.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

const runtimeConfig = (window as Window & { __appConfig?: { primeNgLicense?: string } }).__appConfig;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    'https://graph.microsoft.com/v1.0/me',
    ['user.read']
  );
  if (environment.engtoolapi) {
    protectedResourceMap.set(environment.engtoolapi, [...environment.apiScopes]);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiScopes],
    },
    loginFailedRoute: '/login-failed',
  };
}
export function initializeMsal(msalInstance: IPublicClientApplication) {
  return async () => {
    await msalInstance.initialize();
    console.log('MSAL initialized.');
    await msalInstance.handleRedirectPromise();
  };
}
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig!.auth.clientId,
      authority: environment.msalConfig!.auth.authority,
      redirectUri: environment.msalConfig!.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig!.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}
@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [

    NgxsModule.forRoot([UserState, ActionRequiredState, AppEnvironmentState, PermissionsState, FeatureFlagConfigurationState], {
      compatibility: {
        strictContentSecurityPolicy: true,
      },
    }),
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      }
    }),
    SharedModule,
    BrowserAnimationsModule,
    OfflineHeaderModule,
    SidebarV3Component,
    GeaLibraryModule,
    PrimeNGModule
  ],
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([cSInterceptorInterceptor])),
    {
      provide: LIBRARY_CONFIG,
      useFactory: () => {
        return {

          baseUrl: "http://localhost:4200/",
          legalDocumentsStorageURL: "http://localhost:4200/",
          iconStorageURL: "assets/icons/"
        };
      },
    },
    { provide: 'portalLink', useValue: environment.msalConfig!.auth.redirectUri },
    { provide: 'redirectLink', useValue: environment.msalConfig!.auth.redirectUri },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE],
      multi: true
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    providePrimeNG({
      license: runtimeConfig?.primeNgLicense ?? ''
    }),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
